import { WarningCard } from '@automata/ui';
import { useFlag } from '@automata/utils/hooks';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import size from 'lodash/size';
import { useCallback, useEffect } from 'react';

import { selectLabwareColorIndexById } from '../../../../../features/selectors';
import { updateTaskProperties } from '../../../../../features/workflow/workflowSlice';
import { selectWorkflowLevelErrorsForTaskId } from '../../../../../features/workflowJsonSelectors/workflowLevelErrors';
import { useAppDispatch, useAppSelector } from '../../../../../hooks';
import { useActionTask } from '../../../../../hooks/tasks';
import { PropertiesType } from '../../../../../types';
import { getColorFromIndex } from '../../../../../utils';
import {
  InstrumentCapability,
  hasCapability,
  supportsSlotRanges,
} from '../../../../../utils/instrumentCapabilities';
import { getRangeSize, parseSlotSpec } from '../../../../../utils/slots';
import { FormSectionLabel } from '../../FormSectionLabel';
import HeaderCell from '../../HeaderCell/HeaderCell';
import LabwareCellContent from '../../LabwareCellContent/LabwareCellContent';
import SlotField from '../../LabwareFields/SlotField';
import PropertiesTableStyled from '../../PropertiesTableStyled/PropertiesTableStyled';
import { NoLabware } from '../NoLabware';
import { setDefaultSlotValue } from '../utils';

type HamiltonStarletSlotAllocationsProps = {
  taskId: string;
};

type RawSlotMap = Record<string, string | number | null | undefined>;

const normalizeSlotMap = (m?: RawSlotMap): Record<string, string> => {
  const out: Record<string, string> = {};
  if (!m) return out;
  for (const [k, v] of Object.entries(m)) {
    if (v === null || v === undefined) continue;
    out[k] = String(v);
  }
  return out;
};

const toNumber = (v: string | number | null | undefined): number => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 1;
  const n = parseInt(String(v ?? '1'), 10);
  return Number.isFinite(n) ? n : 1;
};

const toSlotSpecString = (v: string | number | null | undefined): string => {
  if (v == null) return '1';
  const s = String(v).trim();
  return s === '' ? '1' : s;
};

export const LabwareSlotAllocation = ({
  taskId,
}: HamiltonStarletSlotAllocationsProps) => {
  const dispatch = useAppDispatch();
  const task = useActionTask(taskId);
  const colorIndexes = useAppSelector(selectLabwareColorIndexById);
  const workflowLevelErrorsVisible = useFlag(
    'fe.canvas-builder-workflow-errors-visible'
  );
  const workflowLevelErrors = useAppSelector((state) =>
    selectWorkflowLevelErrorsForTaskId(state, taskId)
  );
  const instrument = task.instrument;

  useEffect(() => {
    if (!task.labware || !task.instrument) return;

    const { capacity } = task.instrument;
    const { labwareIdsToInputSlots, labwareIdsToOutputSlots, labware } = task;

    const supportsRanges = supportsSlotRanges(task.instrument.driverName ?? '');

    const actualIn: Record<string, string | number> = {};
    const actualOut: Record<string, string | number> = {};
    let needsUpdate = false;

    labware.forEach(({ id }) => {
      const currentIn =
        labwareIdsToInputSlots?.[id] ??
        setDefaultSlotValue(capacity, normalizeSlotMap(labwareIdsToInputSlots));
      const currentOut =
        labwareIdsToOutputSlots?.[id] ??
        setDefaultSlotValue(
          capacity,
          normalizeSlotMap(labwareIdsToOutputSlots)
        );

      actualIn[id] = supportsRanges
        ? toSlotSpecString(currentIn)
        : toNumber(currentIn);
      actualOut[id] = supportsRanges
        ? toSlotSpecString(currentOut)
        : toNumber(currentOut);

      if (labwareIdsToInputSlots?.[id] === undefined) needsUpdate = true;
      if (labwareIdsToOutputSlots?.[id] === undefined) needsUpdate = true;
    });

    if (
      size(actualIn) !== size(labwareIdsToInputSlots) ||
      size(actualOut) !== size(labwareIdsToOutputSlots)
    ) {
      needsUpdate = true;
    }

    if (needsUpdate) {
      dispatch(
        updateTaskProperties({
          id: task.id,
          propertiesType: PropertiesType.Action,
          labwareIdsToInputSlots: actualIn,
          labwareIdsToOutputSlots: actualOut,
        })
      );
    }
  }, [dispatch, task]);

  const handleUpdateSlotAllocation = useCallback(
    ({
      labwareId,
      slotSpec,
      slotType,
    }: {
      labwareId: string;
      slotSpec: string;
      slotType: 'input' | 'output';
    }) => {
      // Allow empty field while user is typing - don't dispatch updates for empty values
      const trimmed = (slotSpec ?? '').trim();
      if (trimmed === '') {
        return;
      }

      const parameterKey =
        slotType === 'input'
          ? 'labwareIdsToInputSlots'
          : 'labwareIdsToOutputSlots';
      const existingValues =
        slotType === 'input'
          ? task.labwareIdsToInputSlots
          : task.labwareIdsToOutputSlots;

      const isOutputSlotVisible = hasCapability(
        task.instrument?.driverName || '',
        InstrumentCapability.OutputSlotRequired
      );

      const normalized = trimmed;

      if (existingValues?.[labwareId] === normalized) {
        return;
      }

      let properties: Record<string, unknown> = {
        [parameterKey]: {
          ...existingValues,
          [labwareId]: normalized,
        },
      };

      if (slotType === 'input' && !isOutputSlotVisible) {
        if (task.labwareIdsToOutputSlots?.[labwareId] !== normalized) {
          properties = {
            ...properties,
            labwareIdsToOutputSlots: {
              ...task.labwareIdsToOutputSlots,
              [labwareId]: normalized,
            },
          };
        }
      }

      dispatch(
        updateTaskProperties({
          id: taskId,
          propertiesType: PropertiesType.Action,
          ...properties,
        })
      );
    },
    [dispatch, taskId, task]
  );

  if (!instrument) return null;

  const supportsRanges = supportsSlotRanges(instrument.driverName ?? '');
  const isOutputSlotVisible = hasCapability(
    instrument?.driverName,
    InstrumentCapability.OutputSlotRequired
  );

  // Generic validation function that works for both Hamilton and Biomek
  const validateSlotInput = (slotSpec: string | null): string | undefined => {
    const spec = slotSpec ?? '1';
    const expanded = parseSlotSpec(spec);
    const capacity = instrument?.capacity;

    if (capacity && expanded.some((s) => s < 1 || s > capacity)) {
      return 'out of range';
    }

    // Note: Slot collision validation is handled by workflow-level validation
    // to avoid duplicate error messages
  };

  // Generic function to get slot fields - now returns string for both instruments
  const getSlotFields = (labwareId: string) => {
    const inputSlot = task.labwareIdsToInputSlots?.[labwareId];
    const outputSlot = task.labwareIdsToOutputSlots?.[labwareId];

    return [
      {
        slotType: 'input' as const,
        slot: inputSlot?.toString() ?? '',
      },
      {
        slotType: 'output' as const,
        slot: outputSlot?.toString() ?? '',
      },
    ];
  };

  if (task.labware === null || task.labware.length === 0) {
    return (
      <Box paddingY={2}>
        <FormSectionLabel>Labware & Deck</FormSectionLabel>
        {!task.isLabwareLessTask && (
          <WarningCard message="No labware connected" />
        )}
        <NoLabware
          isLabwareLessTask={task.isLabwareLessTask}
          taskId={task.id}
        />
      </Box>
    );
  }

  return (
    <Box paddingY={2}>
      <FormSectionLabel>Labware & Deck</FormSectionLabel>
      <Stack>
        <PropertiesTableStyled>
          <TableHead>
            <TableRow>
              <HeaderCell>Labware</HeaderCell>
              <HeaderCell tooltipText="Starting position for the labware. This must match the expected start position for the method. The number is the LINQ reference, not the liquid handler deck reference, and only needs to be specified for the robot accessible positions.">
                Start
              </HeaderCell>
              {isOutputSlotVisible && (
                <HeaderCell tooltipText="Ending position for the labware. This must match the expected final position for the method. The number is the LINQ reference, not the liquid handler deck reference, and only needs to be specified for the robot accessible positions.">
                  End
                </HeaderCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {task.labware?.map((labware) => (
              <TableRow key={`${task.id}-${labware.id}`}>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <div>
                    <LabwareCellContent
                      color={getColorFromIndex(colorIndexes[labware.id] ?? 0)}
                    >
                      {labware.nickname}
                    </LabwareCellContent>
                  </div>
                </TableCell>

                {getSlotFields(labware.id)
                  .filter(
                    ({ slotType }) =>
                      slotType === 'input' || isOutputSlotVisible
                  )
                  .map(({ slot, slotType }) => {
                    const localError = validateSlotInput(slot);
                    let lengthError: string | undefined;
                    if (slotType === 'input' && isOutputSlotVisible) {
                      const startSpec = String(
                        task.labwareIdsToInputSlots?.[labware.id] ?? '1'
                      );
                      const endSpec = String(
                        task.labwareIdsToOutputSlots?.[labware.id] ?? '1'
                      );
                      const bothRanges =
                        getRangeSize(startSpec) > 1 &&
                        getRangeSize(endSpec) > 1;
                      const lenMismatch =
                        bothRanges &&
                        getRangeSize(startSpec) !== getRangeSize(endSpec);
                      lengthError = lenMismatch
                        ? 'start/end range sizes must match'
                        : undefined;
                    }
                    return (
                      <TableCell
                        key={`${task.id}-${labware.id}-${slotType}`}
                        sx={{ verticalAlign: 'top' }}
                      >
                        <SlotField
                          id={`${task.id}-${labware.id}-${slotType}`}
                          value={slot}
                          onChange={(v) =>
                            handleUpdateSlotAllocation({
                              labwareId: labware.id,
                              slotSpec: v,
                              slotType,
                            })
                          }
                          showLabel={false}
                          isRange={supportsRanges}
                          helperText={localError ?? lengthError}
                          errors={
                            workflowLevelErrorsVisible
                              ? workflowLevelErrors
                              : []
                          }
                        />
                      </TableCell>
                    );
                  })}
              </TableRow>
            ))}
          </TableBody>
        </PropertiesTableStyled>
      </Stack>
    </Box>
  );
};
